import {
  Box,
  Button,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormErrorMessage,
  HStack,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { Attachment, CurrencyCode, Transaction } from "../../../API";
import StatusChip from "../../../components/status-chip";
import { formatAmount, formatDate } from "../../../utils";
import ReceiptPreview from "./receipt-preview";

/** Enriched transaction type used by the approvals page */
type ApprovalTransaction = Transaction & {
  userName: string;
  bookingAccountName: string;
};

type ApprovalDetailDrawerProps = {
  transaction: ApprovalTransaction;
  isOpen: boolean;
  onClose: () => void;
  onApprove: (transactionId: string) => void;
  onReject: (transactionId: string, comment: string) => void;
  onRequestChanges: (transactionId: string, comment: string) => void;
  isPending: boolean;
};

const ApprovalDetailDrawer = ({
  transaction,
  isOpen,
  onClose,
  onApprove,
  onReject,
  onRequestChanges,
  isPending,
}: ApprovalDetailDrawerProps) => {
  const [comment, setComment] = useState("");
  const [showError, setShowError] = useState(false);

  // Reset comment state when transaction changes (auto-advance swaps without closing drawer)
  useEffect(() => {
    setComment("");
    setShowError(false);
  }, [transaction.id]);

  const approvalStatus = transaction.approvalStatus;

  // Extract attachments from the connection
  const attachments: Attachment[] =
    (transaction.attachments?.items?.filter(Boolean) as Attachment[]) ?? [];

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="lg">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px">
          <VStack alignItems="start" spacing="1">
            <Text fontSize="lg" fontWeight="semibold">
              {transaction.title}
            </Text>
            <HStack>
              {approvalStatus && (
                <StatusChip type="approval" status={approvalStatus} />
              )}
            </HStack>
          </VStack>
        </DrawerHeader>

        <DrawerBody>
          <VStack spacing="4" alignItems="start" pt="4">
            {/* Transaction details */}
            <DetailRow
              label="Datum"
              value={formatDate(new Date(transaction.paidAt))}
            />
            <DetailRow
              label="Betrag"
              value={formatAmount(transaction.amount, transaction.currencyCode)}
            />
            {transaction.baseCurrencyAmount && (
              <DetailRow
                label="CHF Betrag"
                value={formatAmount(
                  transaction.baseCurrencyAmount,
                  CurrencyCode.chf
                )}
              />
            )}
            {transaction.exchangeRate && (
              <DetailRow
                label="Wechselkurs"
                value={transaction.exchangeRate.toString()}
              />
            )}
            <DetailRow
              label="Buchungskategorie"
              value={transaction.bookingAccountName}
            />
            <DetailRow label="Mitarbeiter" value={transaction.userName} />

            {/* Receipt preview */}
            <ReceiptPreview
              attachments={attachments}
              transaction={transaction}
            />

            <Divider />

            {/* Comment input for actions */}
            {isPending && (
              <>
                <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                  Kommentar
                </Text>
                <FormControl isInvalid={showError && !comment.trim()}>
                  <Textarea
                    placeholder="Kommentar hinzufügen (erforderlich für Ablehnung und Änderungsanfrage)..."
                    value={comment}
                    onChange={(event) => {
                      setComment(event.target.value);
                      setShowError(false);
                    }}
                    size="sm"
                    resize="vertical"
                  />
                  <FormErrorMessage fontSize="xs" color="error.600">
                    Kommentar ist erforderlich für Ablehnung und
                    Änderungsanfragen
                  </FormErrorMessage>
                </FormControl>
              </>
            )}
          </VStack>
        </DrawerBody>

        {isPending && (
          <DrawerFooter borderTopWidth="1px">
            <VStack width="full" spacing="2" alignItems="center">
              <Flex gap="3">
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="warning"
                  onClick={() => {
                    if (!comment.trim()) {
                      setShowError(true);
                      return;
                    }
                    onRequestChanges(transaction.id, comment);
                    setComment("");
                    setShowError(false);
                  }}
                >
                  Änderungen anfragen
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="error"
                  onClick={() => {
                    if (!comment.trim()) {
                      setShowError(true);
                      return;
                    }
                    onReject(transaction.id, comment);
                    setComment("");
                    setShowError(false);
                  }}
                >
                  Ablehnen
                </Button>
                <Button
                  size="sm"
                  colorScheme="primary"
                  onClick={() => {
                    onApprove(transaction.id);
                    setComment("");
                    setShowError(false);
                  }}
                >
                  Genehmigen
                </Button>
              </Flex>
              <Text fontSize="xs" color="gray.400">
                G Genehmigen · ↑↓ Navigieren · Esc Schliessen
              </Text>
            </VStack>
          </DrawerFooter>
        )}
      </DrawerContent>
    </Drawer>
  );
};

const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <Flex width="full" justifyContent="space-between" alignItems="center">
    <Text fontSize="sm" color="gray.500" fontWeight="medium">
      {label}
    </Text>
    <Text fontSize="sm" color="gray.800">
      {value}
    </Text>
  </Flex>
);

export default ApprovalDetailDrawer;
